import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'
import type { TrackId } from '@/constants/studio'

import { useEffect, useId, useRef, useState } from 'react'
import { ChevronDown, CircleCheck, LoaderCircle, Send } from 'lucide-react'
import { cn } from '@/lib/utils'

import {
    GRADES,
    TRACKS,
    TRIAL_LIMITS,
    UNSURE_TRACK_LABEL
} from '@/constants/studio'

interface TrialFormProps {
    track: TrackId | ''
    onTrackChange: (track: TrackId | '') => void
    focusSignal: number
}

interface FieldProps {
    id: string
    label: string
    optional?: boolean
    error?: string
    children: ReactNode
}

type FieldName = 'parentName' | 'phone' | 'email' | 'childName' | 'grade' | 'notes'

type Status = 'idle' | 'submitting' | 'success' | 'error'

type Values = Record<FieldName | 'website', string>

const EMPTY: Values = {
    parentName: '',
    phone: '',
    email: '',
    childName: '',
    grade: '',
    notes: '',
    website: ''
}

const FIELD_ORDER: (FieldName | 'track')[] = ['parentName', 'phone', 'email', 'childName', 'grade', 'track', 'notes']

const inputClass = cn(
    'w-full min-h-12 rounded-xl border border-input bg-background/60 px-4 text-base text-foreground transition-colors duration-200',
    'placeholder:text-muted-foreground/70 focus:border-primary/60 focus:outline-none focus:ring-2 focus:ring-ring/40',
    'aria-[invalid=true]:border-red-400/70'
)

const validate = (values: Values): Record<string, string> => {
    const errors: Record<string, string> = {}
    const digits = values.phone.replace(/\D/g, '')

    if (values.parentName.trim().length < 2) errors.parentName = 'נשמח לדעת איך קוראים לכם.'
    if (!/^[0-9+\-\s()]+$/.test(values.phone.trim()) || digits.length < 9 || digits.length > 15) errors.phone = 'מספר הטלפון לא נראה תקין.'
    if (values.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(values.email.trim())) errors.email = 'כתובת האימייל לא נראית תקינה.'
    if (values.childName.trim().length < 2) errors.childName = 'מה שם הילד או הילדה?'
    if (!values.grade) errors.grade = 'בחרו כיתה.'

    return errors
}

const Field: FC<FieldProps> = ({ id, label, optional, error, children }): ReactNode => {
    return (
        <div className='space-y-1.5'>
            <label htmlFor={id} className='flex items-baseline justify-between gap-3 text-sm font-medium text-foreground'>
                <span>
                    {label}
                </span>

                {optional && (
                    <span className='text-xs font-normal text-muted-foreground'>
                        לא חובה
                    </span>
                )}
            </label>

            {children}

            {error && (
                <p id={`${id}-error`} className='text-sm text-red-300'>
                    {error}
                </p>
            )}
        </div>
    )
}

const TrialForm: FC<TrialFormProps> = ({ track, onTrackChange, focusSignal }): ReactNode => {
    const formId = useId()
    const [values, setValues] = useState<Values>(EMPTY)
    const [errors, setErrors] = useState<Record<string, string>>({})
    const [status, setStatus] = useState<Status>('idle')
    const [message, setMessage] = useState('')
    const [submittedChild, setSubmittedChild] = useState('')
    const successRef = useRef<HTMLHeadingElement>(null)

    const idOf = (field: FieldName | 'track'): string => `${formId}-${field}`

    useEffect(() => {
        if (status === 'success') successRef.current?.focus()
    }, [status])

    useEffect(() => {
        if (focusSignal > 0) document.getElementById(`${formId}-parentName`)?.focus({ preventScroll: true })
    }, [focusSignal, formId])

    const clearError = (field: string): void => {
        if (!errors[field]) return

        setErrors((current) => {
            const next = { ...current }
            delete next[field]
            return next
        })
    }

    const update = (field: FieldName | 'website') => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
        const { value } = event.target

        setValues((current) => ({ ...current, [field]: value }))
        clearError(field)
    }

    const onGradeChange = (event: ChangeEvent<HTMLSelectElement>): void => {
        const previous = GRADES.find((option) => option.value === values.grade)?.track ?? ''
        const next = GRADES.find((option) => option.value === event.target.value)?.track ?? ''

        // suggest the matching group, unless a group was picked by hand
        if (!track || track === previous) onTrackChange(next)

        update('grade')(event)
    }

    const describedBy = (field: FieldName): string | undefined => {
        return errors[field] ? `${idOf(field)}-error` : undefined
    }

    const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        if (status === 'submitting') return

        const found = validate(values)

        if (Object.keys(found).length > 0) {
            setErrors(found)
            setStatus('idle')
            setMessage('')

            const first = FIELD_ORDER.find((field) => found[field])
            if (first) document.getElementById(idOf(first))?.focus()
            return
        }

        setStatus('submitting')
        setMessage('')

        try {
            const response = await fetch('/api/trial-signup', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...values, track })
            })

            const data = (await response.json().catch(() => null)) as { ok?: boolean, message?: string, fields?: Record<string, string> } | null

            if (response.ok && data?.ok) {
                setSubmittedChild(values.childName.trim())
                setValues(EMPTY)
                setErrors({})
                setStatus('success')
                return
            }

            if (data?.fields && Object.keys(data.fields).length > 0) setErrors(data.fields)

            setStatus('error')
            setMessage(data?.message ?? 'לא הצלחנו לשלוח את הפרטים. נסו שוב בעוד רגע.')
        }
        catch {
            setStatus('error')
            setMessage('נראה שיש בעיה בחיבור לאינטרנט. בדקו את החיבור ונסו שוב.')
        }
    }

    const reset = (): void => {
        setStatus('idle')
        setSubmittedChild('')
        onTrackChange('')
    }

    if (status === 'success') {
        return (
            <div role='status' className='flex flex-col items-center py-10 text-center'>
                <CircleCheck aria-hidden='true' className='size-14 text-primary' />

                <h3
                    ref={successRef}
                    tabIndex={-1}
                    className='mt-5 font-heading text-3xl font-bold focus:outline-none sm:text-4xl'
                >
                    קיבלנו, תודה!
                </h3>

                <p className='mt-3 max-w-sm leading-relaxed text-muted-foreground'>
                    {`נחזור אליכם בהקדם כדי לתאם את שיעור הניסיון של ${submittedChild}. בינתיים אפשר להתחיל לחמם את הקול.`}
                </p>

                <button
                    type='button'
                    onClick={reset}
                    className='mt-8 inline-flex min-h-12 items-center rounded-full border border-border px-6 text-sm font-semibold text-foreground transition-colors duration-300 hover:border-primary/50 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                >
                    רישום ילד או ילדה נוספים
                </button>
            </div>
        )
    }

    return (
        <form noValidate onSubmit={onSubmit} className='relative space-y-5'>
            <div className='grid gap-5 sm:grid-cols-2'>
                <Field id={idOf('parentName')} label='שם ההורה' error={errors.parentName}>
                    <input
                        id={idOf('parentName')}
                        name='parentName'
                        type='text'
                        autoComplete='name'
                        maxLength={TRIAL_LIMITS.name}
                        value={values.parentName}
                        onChange={update('parentName')}
                        aria-invalid={Boolean(errors.parentName)}
                        aria-describedby={describedBy('parentName')}
                        className={inputClass}
                    />
                </Field>

                <Field id={idOf('phone')} label='טלפון' error={errors.phone}>
                    <input
                        id={idOf('phone')}
                        name='phone'
                        type='tel'
                        inputMode='tel'
                        autoComplete='tel'
                        maxLength={TRIAL_LIMITS.phone}
                        value={values.phone}
                        onChange={update('phone')}
                        aria-invalid={Boolean(errors.phone)}
                        aria-describedby={describedBy('phone')}
                        className={inputClass}
                    />
                </Field>
            </div>

            <Field id={idOf('email')} label='אימייל' optional error={errors.email}>
                <input
                    id={idOf('email')}
                    name='email'
                    type='email'
                    dir='ltr'
                    autoComplete='email'
                    maxLength={TRIAL_LIMITS.email}
                    value={values.email}
                    onChange={update('email')}
                    aria-invalid={Boolean(errors.email)}
                    aria-describedby={describedBy('email')}
                    className={cn(inputClass, 'text-end')}
                />
            </Field>

            <div className='grid gap-5 sm:grid-cols-2'>
                <Field id={idOf('childName')} label='שם הילד או הילדה' error={errors.childName}>
                    <input
                        id={idOf('childName')}
                        name='childName'
                        type='text'
                        autoComplete='off'
                        maxLength={TRIAL_LIMITS.name}
                        value={values.childName}
                        onChange={update('childName')}
                        aria-invalid={Boolean(errors.childName)}
                        aria-describedby={describedBy('childName')}
                        className={inputClass}
                    />
                </Field>

                <Field id={idOf('grade')} label='כיתה' error={errors.grade}>
                    <div className='relative'>
                        <select
                            id={idOf('grade')}
                            name='grade'
                            value={values.grade}
                            onChange={onGradeChange}
                            aria-invalid={Boolean(errors.grade)}
                            aria-describedby={describedBy('grade')}
                            className={cn(inputClass, 'appearance-none pe-11')}
                        >
                            <option value='' disabled>
                                בחרו כיתה
                            </option>
                            {GRADES.map((grade) => (
                                <option key={grade.value} value={grade.value}>
                                    {grade.label}
                                </option>
                            ))}
                        </select>
                        <ChevronDown aria-hidden='true' className='pointer-events-none absolute end-4 top-1/2 size-4 -translate-y-1/2 text-muted-foreground' />
                    </div>
                </Field>
            </div>

            <Field id={idOf('track')} label='קבוצה'>
                <div className='relative'>
                    <select
                        id={idOf('track')}
                        name='track'
                        value={track}
                        onChange={(event) => onTrackChange(event.target.value as TrackId | '')}
                        className={cn(inputClass, 'appearance-none pe-11')}
                    >
                        <option value=''>
                            {UNSURE_TRACK_LABEL}
                        </option>
                        {TRACKS.map((option) => (
                            <option key={option.id} value={option.id}>
                                {`${option.name} (${option.grades})`}
                            </option>
                        ))}
                    </select>
                    <ChevronDown aria-hidden='true' className='pointer-events-none absolute end-4 top-1/2 size-4 -translate-y-1/2 text-muted-foreground' />
                </div>
            </Field>

            <Field id={idOf('notes')} label='משהו שחשוב לנו לדעת?' optional>
                <textarea
                    id={idOf('notes')}
                    name='notes'
                    rows={3}
                    maxLength={TRIAL_LIMITS.notes}
                    value={values.notes}
                    onChange={update('notes')}
                    placeholder='ניסיון קודם, ימים שנוחים לכם או כל שאלה'
                    className={cn(inputClass, 'min-h-24 resize-y py-3')}
                />
            </Field>

            <div aria-hidden='true' className='absolute -start-[9999px] top-0 size-px overflow-hidden'>
                <label htmlFor={`${formId}-website`}>
                    אתר
                </label>
                <input
                    id={`${formId}-website`}
                    name='website'
                    type='text'
                    tabIndex={-1}
                    autoComplete='off'
                    value={values.website}
                    onChange={update('website')}
                />
            </div>

            {status === 'error' && message && (
                <p role='alert' className='rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-200'>
                    {message}
                </p>
            )}

            <button
                type='submit'
                disabled={status === 'submitting'}
                className='inline-flex min-h-13 w-full items-center justify-center gap-2 rounded-full bg-primary px-7 text-base font-semibold text-primary-foreground shadow-[0_12px_32px_-12px_oklch(0.8_0.12_80/0.7)] transition-all duration-300 hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-wait disabled:opacity-80'
            >
                {status === 'submitting'
                    ? <LoaderCircle aria-hidden='true' className='size-5 animate-spin' />
                    : <Send aria-hidden='true' className='size-5' />}
                {status === 'submitting' ? 'שולחים את הפרטים' : 'שליחה והרשמה לשיעור ניסיון'}
            </button>

            <p className='text-center text-xs leading-relaxed text-muted-foreground'>
                נשתמש בפרטים רק כדי לחזור אליכם בנוגע לשיעור הניסיון.
            </p>
        </form>
    )
}

export default TrialForm
